import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  IndianRupee, ArrowUpRight, Download, Phone, MapPin, MessageCircle,
  Mic, Armchair, Sparkles, Clock, Wind, Car, Camera, Film, Users,
} from "lucide-react";
import { Nav, Footer } from "@/pages/LimeLanding";
import BookingDialog from "@/components/BookingDialog";
import WhatsAppButton from "@/components/WhatsAppButton";
import {
  FEATURES, PHONE_DISPLAY, WHATSAPP_NUMBER, STUDIO_ADDRESS,
} from "@/lib/lime-data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PDF_URL = "/LIME-Studios-Pricing.pdf";
const ICONS = { Mic, Armchair, Sparkles, Clock, Wind, Car, Camera, Film, Users };

const CATEGORY_LABEL = {
  photoshoot: "Photoshoot",
  podcast: "Podcast",
  video: "Video",
};

// For packages that have a "Per Hour" option, expand the option list to a
// full 1-8 hour breakdown using the same combo-pricing logic the booking
// dialog uses. Fixed-tier packages (which don't include Per Hour) are shown
// as-is.
const expandOptions = (p) => {
  const perHour = p.options.find((o) => /per hour/i.test(o.duration));
  if (!perHour) return p.options;
  const perHourRate = perHour.price;
  const fixedTiers = p.options
    .filter((o) => o !== perHour)
    .map((o) => {
      const m = /(\d+)\s*hour/i.exec(o.duration);
      return m ? { hours: parseInt(m[1], 10), price: o.price } : null;
    })
    .filter(Boolean)
    .sort((a, b) => b.hours - a.hours); // largest first for greedy combo

  const bestFor = (h) => {
    // Exact fixed-tier match wins
    const exact = fixedTiers.find((t) => t.hours === h);
    if (exact) return exact.price;
    // Greedy combo of fixed tiers + per-hour leftover
    let remaining = h;
    let total = 0;
    for (const tier of fixedTiers) {
      while (remaining >= tier.hours) { total += tier.price; remaining -= tier.hours; }
    }
    total += remaining * perHourRate;
    // Cap at pure per-hour total (never worse than paying by the hour)
    return Math.min(total, h * perHourRate);
  };

  const maxHours = Math.max(8, ...fixedTiers.map((t) => t.hours));
  return Array.from({ length: maxHours }, (_, i) => ({
    duration: `${i + 1} Hour${i > 0 ? "s" : ""}`,
    price: bestFor(i + 1),
  }));
};

const PackageCard = ({ p, onBook }) => {
  const displayOptions = expandOptions(p);
  return (
  <div
    data-testid={`pricing-page-card-${p.id}`}
    className={`relative border bg-[#0a0a0a] p-7 flex flex-col transition-all duration-300 ${
      p.popular
        ? "border-[#E4FF00]/60 glow-yellow"
        : "border-[#262626] hover:border-[#404040]"
    }`}
  >
    {p.popular && (
      <Badge className="absolute -top-3 left-7 bg-[#E4FF00] text-black font-mono text-[10px] uppercase tracking-widest rounded-none">
        Most Booked
      </Badge>
    )}
    <div className="font-mono-overline text-[#A3A3A3]">
      {CATEGORY_LABEL[p.category] || p.category}
    </div>
    <h3 className="font-display text-2xl font-black tracking-tight mt-2">{p.name}</h3>
    <p className="text-sm text-[#A3A3A3] mt-2 leading-relaxed">{p.description}</p>

    <div className="mt-6 space-y-2" data-testid={`pricing-page-options-${p.id}`}>
      {displayOptions.map((o) => (
        <div
          key={o.duration}
          className="flex items-center justify-between border-b border-[#262626] pb-2"
        >
          <span className="text-xs font-mono uppercase tracking-wider text-[#A3A3A3]">
            {o.duration}
          </span>
          <span className="font-display text-lg font-bold flex items-center text-[#E4FF00]">
            <IndianRupee className="h-4 w-4" />
            {o.price.toLocaleString("en-IN")}
          </span>
        </div>
      ))}
    </div>

    <div className="mt-6 flex-1">
      <div className="font-mono-overline text-[#E4FF00] mb-2">Includes</div>
      <ul className="space-y-2 text-sm text-[#A3A3A3]">
        {p.includes.map((it) => (
          <li key={it} className="flex gap-2">
            <span className="text-[#E4FF00] shrink-0">+</span>{it}
          </li>
        ))}
      </ul>
    </div>

    <div className="mt-5 text-[11px] font-mono uppercase tracking-widest text-[#666]">
      Minimum booking · {p.min_hours || 1} hour{(p.min_hours || 1) > 1 ? "s" : ""}
    </div>

    <Button
      onClick={() => onBook(p)}
      data-testid={`pricing-page-book-${p.id}`}
      className="mt-6 bg-[#E4FF00] text-black hover:bg-[#CDE600] rounded-none font-bold w-full"
    >
      Book this package <ArrowUpRight className="ml-1 h-4 w-4" />
    </Button>
  </div>
  );
};

export default function PricingPage() {
  const [packages, setPackages] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [initialPkg, setInitialPkg] = useState(null);

  useEffect(() => {
    document.title = "Pricing · LIME Studios";
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/packages`);
        setPackages(data);
      } catch (e) {
        toast.error("Couldn't load packages");
      }
    };
    load();
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const openBooking = (pkg) => {
    setInitialPkg(pkg || null);
    setOpenDialog(true);
  };

  return (
    <div className="bg-[#0a0a0a] text-[#f4f4f0] min-h-screen">
      <Nav onBook={() => openBooking()} />

      {/* Hero */}
      <section className="pt-32 sm:pt-40 pb-14 border-b border-[#262626]" data-testid="pricing-page-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="font-mono-overline text-[#E4FF00]">Transparent pricing · 2026</div>
          <h1 className="font-display text-5xl sm:text-7xl lg:text-[8rem] font-black leading-[0.9] tracking-tighter mt-4">
            <span className="text-[#f4f4f0]">Packages</span>
            <span className="text-[#E4FF00]">.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg text-[#A3A3A3] leading-relaxed">
            Pro-grade equipment, a professional crew and a hassle-free,
            sound-treated studio experience — for content creators, filmmakers,
            brands, agencies and freelancers.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              onClick={() => openBooking()}
              data-testid="pricing-page-hero-book"
              className="bg-[#E4FF00] text-black hover:bg-[#CDE600] rounded-none font-bold px-6 py-6 text-base"
            >
              Book your slot <ArrowUpRight className="ml-2 h-5 w-5" />
            </Button>
            <a
              href={PDF_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pricing-page-download-pdf"
              className="inline-flex items-center gap-2 border border-[#262626] hover:border-[#E4FF00] text-[#f4f4f0] hover:text-[#E4FF00] px-6 py-4 font-mono text-xs uppercase tracking-widest transition-colors"
            >
              <Download className="h-4 w-4" /> Download PDF
            </a>
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hi LIME Studios, I'd like to know more about your pricing.")}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pricing-page-whatsapp"
              className="inline-flex items-center gap-2 border border-[#262626] hover:border-[#E4FF00] text-[#f4f4f0] hover:text-[#E4FF00] px-6 py-4 font-mono text-xs uppercase tracking-widest transition-colors"
            >
              <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Packages grid */}
      <section className="py-20 sm:py-28 border-b border-[#262626]" data-testid="pricing-page-packages">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {packages.length === 0 ? (
            <div className="text-center py-16 text-[#666] font-mono text-xs uppercase tracking-widest">
              Loading packages…
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {packages.map((p) => (
                <PackageCard key={p.id} p={p} onBook={openBooking} />
              ))}
            </div>
          )}

          <p className="mt-8 text-xs font-mono uppercase tracking-widest text-[#666]">
            * Prices exclusive of GST · Video editing add-ons available · Custom packages on request
          </p>
        </div>
      </section>

      {/* Why LIME strip */}
      <section className="py-20 sm:py-28 border-b border-[#262626]" data-testid="pricing-page-why">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="font-mono-overline text-[#E4FF00]">Why LIME</div>
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-3">
            What comes with every booking.
          </h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f) => {
              const Ico = ICONS[f.icon];
              return (
                <div
                  key={f.label}
                  className="flex items-center gap-4 border border-[#262626] hover:border-[#E4FF00]/60 transition-colors p-5"
                  data-testid={`pricing-feature-${f.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {Ico && (
                    <span className="shrink-0 h-10 w-10 flex items-center justify-center border border-[#262626] text-[#E4FF00]">
                      <Ico className="h-5 w-5" />
                    </span>
                  )}
                  <span className="text-sm text-[#f4f4f0]">{f.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Good to know */}
      <section className="py-20 sm:py-28 border-b border-[#262626]" data-testid="pricing-page-notes">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-5">
            <div className="font-mono-overline text-[#E4FF00]">Good to know</div>
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-3">
              The fine print.
            </h2>
          </div>
          <ul className="md:col-span-7 space-y-4 text-[#A3A3A3]">
            {[
              "All prices are exclusive of GST.",
              "Video editing add-ons available on request.",
              "Custom packages available — tell us what you're shooting and we'll build one for you.",
              "24 × 7 availability · air-conditioned · sound-treated · makeup console · dedicated parking.",
            ].map((t) => (
              <li key={t} className="flex gap-3 leading-relaxed">
                <span className="text-[#E4FF00] shrink-0">→</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Contact CTA (lime block, matches PDF) */}
      <section className="py-16 sm:py-20" data-testid="pricing-page-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#E4FF00] text-black p-8 sm:p-14 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-7">
              <div className="font-mono text-[10px] uppercase tracking-widest opacity-70">
                Baner · Pune · 24 × 7
              </div>
              <h2 className="font-display text-4xl sm:text-6xl font-black tracking-tighter mt-2 leading-[0.95]">
                Book your slot.
              </h2>
              <p className="mt-4 text-sm sm:text-base flex items-start gap-2 max-w-md">
                <MapPin className="h-4 w-4 mt-1 shrink-0" />
                <span>{STUDIO_ADDRESS}</span>
              </p>
            </div>
            <div className="md:col-span-5 md:text-right space-y-4">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest opacity-70">
                  WhatsApp / Call
                </div>
                <a
                  href={`tel:${PHONE_DISPLAY.replace(/\s+/g, "")}`}
                  data-testid="pricing-page-call-link"
                  className="font-display text-2xl sm:text-3xl font-black tracking-tighter mt-1 inline-flex items-center gap-2 hover:underline"
                >
                  <Phone className="h-5 w-5" />
                  {PHONE_DISPLAY}
                </a>
              </div>
              <Button
                onClick={() => openBooking()}
                data-testid="pricing-page-cta-book"
                className="bg-black text-[#E4FF00] hover:bg-[#141414] rounded-none font-bold px-6 py-6 text-base"
              >
                Book online now <ArrowUpRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
      <BookingDialog
        open={openDialog}
        onOpenChange={setOpenDialog}
        initialPackage={initialPkg}
        packages={packages}
      />
    </div>
  );
}
